// kubectl 命令解析和自动完成辅助工具

// kubectl 子命令列表
export const KUBECTL_COMMANDS = [
  'get',
  'describe',
  'create',
  'apply',
  'delete',
  'edit',
  'logs',
  'exec',
  'port-forward',
  'top',
  'scale',
  'rollout',
  'set',
  'label',
  'annotate'
];

// Kubernetes 资源类型列表
export const RESOURCE_TYPES = [
  { full: 'pods', short: 'po', description: 'List pods' },
  { full: 'deployments', short: 'deploy', description: 'List deployments' },
  { full: 'services', short: 'svc', description: 'List services' },
  { full: 'namespaces', short: 'ns', description: 'List namespaces' },
  { full: 'configmaps', short: 'cm', description: 'List configmaps' },
  { full: 'secrets', short: '', description: 'List secrets' },
  { full: 'ingress', short: 'ing', description: 'List ingress' },
  { full: 'persistentvolumes', short: 'pv', description: 'List persistent volumes' },
  { full: 'persistentvolumeclaims', short: 'pvc', description: 'List persistent volume claims' },
  { full: 'statefulsets', short: 'sts', description: 'List statefulsets' },
  { full: 'daemonsets', short: 'ds', description: 'List daemonsets' },
  { full: 'replicasets', short: 'rs', description: 'List replicasets' },
  { full: 'nodes', short: 'no', description: 'List nodes' },
  { full: 'events', short: 'ev', description: 'List events' },
  { full: 'all', short: '', description: 'List all resources' }
];

// 常用 kubectl 标志
export const COMMON_FLAGS = [
  { flag: '-n', description: 'namespace' },
  { flag: '--namespace', description: 'namespace' },
  { flag: '-o', description: 'output format' },
  { flag: '--output', description: 'output format' },
  { flag: '-A', description: 'all namespaces' },
  { flag: '--all-namespaces', description: 'all namespaces' },
  { flag: '-w', description: 'watch' },
  { flag: '--watch', description: 'watch' },
  { flag: '-l', description: 'selector' },
  { flag: '--selector', description: 'selector' },
  { flag: '-f', description: 'filename' },
  { flag: '--filename', description: 'filename' },
  { flag: '--dry-run', description: 'dry run' }
];

/**
 * 解析 kubectl 命令
 * @param {string} input - 用户输入的命令
 * @returns {Object} 解析结果
 */
export function parseCommand(input) {
  const trimmed = input.trim();
  const parts = trimmed.split(/\s+/);

  // 支持带或不带 kubectl 前缀的命令
  let startIndex = 0;
  if (parts[0].toLowerCase() === 'kubectl') {
    startIndex = 1;
  }

  const result = {
    isValid: true,
    command: 'kubectl',
    subcommand: null,
    resource: null,
    namespace: null,
    name: null,
    flags: [],
    raw: trimmed
  };

  // 解析子命令
  if (parts.length > startIndex) {
    result.subcommand = parts[startIndex];
  }

  // 解析参数和标志
  let currentPart = startIndex + 1;
  while (currentPart < parts.length) {
    const part = parts[currentPart];

    // 检查是否是标志
    if (part.startsWith('-')) {
      result.flags.push(part);

      // 处理带值的标志
      if (!part.startsWith('--') && part.length === 2 && currentPart + 1 < parts.length) {
        if (!parts[currentPart + 1].startsWith('-')) {
          result.flags.push(parts[currentPart + 1]);
          currentPart++;
        }
      } else if (part.startsWith('--') && currentPart + 1 < parts.length) {
        if (!parts[currentPart + 1].startsWith('-')) {
          result.flags.push(parts[currentPart + 1]);
          currentPart++;
        }
      }
    } else if (!result.resource) {
      result.resource = part;
    } else if (!result.name) {
      result.name = part;
    }

    currentPart++;
  }

  // 从标志中提取命名空间
  const namespaceIndex = result.flags.findIndex(f => f === '-n' || f === '--namespace');
  if (namespaceIndex !== -1 && namespaceIndex + 1 < result.flags.length) {
    result.namespace = result.flags[namespaceIndex + 1];
  }

  return result;
}

/**
 * 从命令中提取命名空间
 * 支持格式：
 * - kubectl get pods -n gravity-core
 * - get pods -n gravity-core
 * - kubectl get pods --namespace gravity-core
 * @param {string} input - 用户输入的命令
 * @returns {string|null} 提取的命名空间，如果没有则返回 null
 */
export function extractNamespaceFromCommand(input) {
  const trimmed = input.trim();
  const parts = trimmed.split(/\s+/);

  // 查找 -n 或 --namespace 标志
  for (let i = 0; i < parts.length; i++) {
    if (parts[i] === '-n' || parts[i] === '--namespace') {
      // 返回下一个参数作为命名空间
      if (i + 1 < parts.length && !parts[i + 1].startsWith('-')) {
        return parts[i + 1];
      }
    }
  }

  return null;
}

/**
 * 清理命令中的命名空间参数
 * 用于在命令中已经指定了命名空间时，移除 -n/--namespace 参数
 * @param {string} input - 原始命令
 * @returns {string} 清理后的命令
 */
export function removeNamespaceFromCommand(input) {
  const trimmed = input.trim();
  const parts = trimmed.split(/\s+/);
  const result = [];

  for (let i = 0; i < parts.length; i++) {
    if (parts[i] === '-n' || parts[i] === '--namespace') {
      // 跳过当前参数和下一个参数
      i++;
      continue;
    }
    result.push(parts[i]);
  }

  return result.join(' ');
}

/**
 * 获取自动完成建议
 * @param {string} input - 当前输入
 * @param {number} cursor - 光标位置
 * @param {Object} context - 上下文信息（集群、命名空间等）
 * @returns {Array} 建议列表
 */
export function getAutocompleteSuggestions(input, cursor, context = {}) {
  const { clusters = [], namespaces = [] } = context;
  const beforeCursor = input.substring(0, cursor);
  const afterCursor = input.substring(cursor);

  // 获取当前正在输入的词
  const words = beforeCursor.split(/\s+/);
  const currentWord = words[words.length - 1] || '';
  const previousWord = words.length > 1 ? words[words.length - 2] : '';

  // 转换为小写进行大小写不敏感匹配
  const currentWordLower = currentWord.toLowerCase();

  // 场景 0: 补全 kubectl 命令本身
  if (words.length === 1 && currentWord && !input.includes(' ')) {
    if ('kubectl'.toLowerCase().startsWith(currentWordLower)) {
      return [{
        text: 'kubectl',
        description: 'Kubernetes command line tool',
        type: 'command'
      }];
    }
    return [];
  }

  // 场景 1: 输入 kubectl 后的第一个词 - 补全子命令
  if (words.length <= 2 && !previousWord) {
    return KUBECTL_COMMANDS
      .filter(cmd => cmd.toLowerCase().startsWith(currentWordLower))
      .map(cmd => ({
        text: cmd,
        description: `kubectl ${cmd} command`,
        type: 'command'
      }));
  }

  // 场景 2: 输入资源类型 - 补全资源
  if (previousWord === 'get' || previousWord === 'describe' || previousWord === 'delete') {
    const suggestions = RESOURCE_TYPES.filter(resource =>
      (resource.full.toLowerCase().startsWith(currentWordLower) || resource.short.toLowerCase().startsWith(currentWordLower))
    ).map(resource => ({
      text: resource.full,
      description: resource.description,
      type: 'resource'
    }));

    if (suggestions.length > 0) {
      return suggestions;
    }
  }

  // 场景 3: 输入 -n 后 - 补全命名空间
  if (previousWord === '-n' || previousWord === '--namespace') {
    return namespaces
      .filter(ns => ns.toLowerCase().startsWith(currentWordLower))
      .map(ns => ({
        text: ns,
        description: 'Namespace',
        type: 'namespace'
      }));
  }

  // 场景 4: 输入标志
  if (currentWord.startsWith('-')) {
    return COMMON_FLAGS
      .filter(f => f.flag.toLowerCase().startsWith(currentWordLower))
      .map(f => ({
        text: f.flag,
        description: f.description,
        type: 'flag'
      }));
  }

  // 场景 5: 补全集群名称（在特定上下文中）
  if (currentWord && context.availableClusters) {
    return context.availableClusters
      .filter(cluster => cluster.toLowerCase().startsWith(currentWordLower))
      .map(cluster => ({
        text: cluster,
        description: 'Cluster',
        type: 'cluster'
      }));
  }

  return [];
}

/**
 * 格式化命令输出
 * @param {string} output - 原始输出
 * @param {string} type - 输出类型
 * @returns {Object} 格式化后的输出
 */
export function formatOutput(output, type = 'output') {
  return {
    type,
    content: output,
    timestamp: new Date().toISOString()
  };
}

/**
 * 验证 kubectl 命令是否安全
 * @param {string} command - 要验证的命令
 * @returns {Object} 验证结果
 */
export function validateCommand(command) {
  const parsed = parseCommand(command);

  // 危险命令列表
  const dangerousCommands = ['delete', 'drain', 'cordon', 'uncordon'];
  const dangerousResources = ['all', 'namespaces', 'nodes'];

  if (parsed.subcommand && dangerousCommands.includes(parsed.subcommand.toLowerCase())) {
    if (parsed.resource?.toLowerCase() === 'all' || dangerousResources.includes(parsed.resource?.toLowerCase())) {
      return {
        isSafe: false,
        warning: `DANGEROUS: ${command} - This could affect multiple resources. Please confirm.`
      };
    }
  }

  return {
    isSafe: true
  };
}

/**
 * 标准化命令 - 确保命令以 kubectl 开头（如果需要）
 * @param {string} input - 用户输入的命令
 * @returns {string} 标准化后的命令
 */
export function normalizeCommand(input) {
  const trimmed = input.trim();
  const parts = trimmed.split(/\s+/);

  // 如果已经以 kubectl 开头，直接返回
  if (parts[0].toLowerCase() === 'kubectl') {
    return trimmed;
  }

  // 否则添加 kubectl 前缀
  return `kubectl ${trimmed}`;
}

/**
 * 生成命令提示符
 * @param {string} cluster - 集群名称
 * @param {string} namespace - 命名空间
 * @returns {string} 提示符
 */
export function generatePrompt(cluster, namespace) {
  const parts = [];

  if (cluster) {
    parts.push(`[${cluster}]`);
  }

  if (namespace) {
    parts.push(`(${namespace})`);
  }

  // 只显示集群和命名空间，不显示 kubectl
  const basePrompt = parts.length > 0 ? parts.join(' ') + ' ' : '';
  return basePrompt + '>';
}

/**
 * 从命令中提取资源名称
 * @param {string} command - kubectl 命令
 * @returns {Array} 资源名称列表
 */
export function extractResourceNames(command) {
  const parsed = parseCommand(command);
  const names = [];

  if (parsed.name) {
    names.push(parsed.name);
  }

  return names;
}
